/**
 * MifeLife v2 — Auth pages (Login + Register).
 *
 * Reuses existing /api/auth/login + /api/auth/register endpoints; the only
 * v2-specific behaviour is the "after" redirect (?after=analiz|mentor|pricing)
 * and the intent field ("Ne için buradasın?") on register.
 */
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Sparkles, Loader2 } from 'lucide-react';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, setToken } from '../../lib/utils';
import { useNoIndex } from '../../hooks/useNoIndex';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogle = () => {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const INTENTS = [
  { value: 'personal', label: 'Bireysel gelişim' },
  { value: 'career', label: 'Kariyer' },
  { value: 'business', label: 'İşletme' },
  { value: 'other', label: 'Diğer' },
];

export function V2LoginPage() {
  useNoIndex();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const after = params.get('after');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await apiRequest('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
      const tok = res?.access_token || res?.token;
      if (tok) setToken(tok);
      nav(after === 'analiz' ? '/analiz' : after === 'mentor' ? '/mentorler' : '/app');
    } catch (e2) {
      setErr(e2?.message || 'Giriş başarısız.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="MifeLife'a dön" sub="E-postanla giriş yap.">
      <form onSubmit={submit} className="space-y-3" data-testid="v2-login-form">
        <AuthField label="E-posta" type="email" value={email} onChange={setEmail} testid="v2-login-email" />
        <AuthField label="Şifre" type="password" value={password} onChange={setPassword} testid="v2-login-password" />
        {err && <div className="text-sm text-rose-300">{err}</div>}
        <button type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="v2-login-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Giriş yap
        </button>
        <button type="button" onClick={startGoogle} className="w-full h-11 rounded-lg border border-white/15 bg-white text-slate-900 text-sm font-medium hover:bg-slate-100 transition flex items-center justify-center gap-2" data-testid="v2-login-google">
          <GoogleIcon /> Google ile devam et
        </button>
      </form>
      <div className="mt-6 text-center text-sm text-slate-400">
        Hesabın yok mu? <Link to="/kayit" className="text-violet-300 hover:text-white">Kayıt ol</Link>
      </div>
    </AuthShell>
  );
}

export function V2RegisterPage() {
  useNoIndex();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const after = params.get('after');
  const plan = params.get('plan');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [intent, setIntent] = useState('personal');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await apiRequest('/auth/register', { method: 'POST', body: JSON.stringify({ email, password, name, selected_product: 'life', intent }) });
      const tok = res?.access_token || res?.token;
      if (tok) setToken(tok);
      // After-routing:
      if (after === 'analiz') nav('/analiz');
      else if (after === 'mentor') nav('/mentorler');
      else if (after === 'pricing') nav('/fiyatlandirma' + (plan ? `?plan=${plan}` : ''));
      else nav('/app');
    } catch (e2) {
      setErr(e2?.message || 'Kayıt başarısız.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell title="Ücretsiz başla" sub="MifeLife: AI yol haritası + gerçek mentörler.">
      <form onSubmit={submit} className="space-y-3" data-testid="v2-register-form">
        <AuthField label="İsim" type="text" value={name} onChange={setName} testid="v2-register-name" />
        <AuthField label="E-posta" type="email" value={email} onChange={setEmail} testid="v2-register-email" />
        <AuthField label="Şifre" type="password" value={password} onChange={setPassword} testid="v2-register-password" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-2">Ne için buradasın?</div>
          <div className="grid grid-cols-2 gap-2">
            {INTENTS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setIntent(opt.value)}
                className={`h-10 px-3 rounded-lg text-sm border transition ${intent === opt.value ? 'bg-violet-500/15 border-violet-400/50 text-white' : 'bg-white/[0.02] border-white/10 text-slate-300 hover:bg-white/[0.05]'}`}
                data-testid={`v2-register-intent-${opt.value}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {err && <div className="text-sm text-rose-300">{err}</div>}
        <button type="submit" disabled={busy} className="w-full h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition flex items-center justify-center gap-2" data-testid="v2-register-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Hesap oluştur
        </button>
        <button type="button" onClick={startGoogle} className="w-full h-11 rounded-lg border border-white/15 bg-white text-slate-900 text-sm font-medium hover:bg-slate-100 transition flex items-center justify-center gap-2" data-testid="v2-register-google">
          <GoogleIcon /> Google ile kayıt ol
        </button>
        <div className="text-[11px] text-slate-500 text-center">Kayıt olarak <Link to="/terms" className="underline">Kullanım Şartları</Link> ve <Link to="/privacy" className="underline">Gizlilik</Link>'i kabul edersin.</div>
      </form>
      <div className="mt-6 text-center text-sm text-slate-400">
        Zaten hesabın var mı? <Link to="/giris" className="text-violet-300 hover:text-white">Giriş yap</Link>
      </div>
    </AuthShell>
  );
}

const AuthShell = ({ title, sub, children }) => (
  <div className="min-h-screen bg-gradient-to-b from-[#0F0F1A] via-[#11111E] to-[#1A1A2E] text-white antialiased" data-testid="v2-auth-shell">
    <V2Nav lang="tr" />
    <section className="pt-32 pb-20">
      <div className="max-w-md mx-auto px-5 sm:px-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-5">
          <Sparkles className="w-3 h-3" /> MifeLife
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-2 text-sm text-slate-400">{sub}</p>
        <div className="mt-8 rounded-2xl border border-white/10 bg-white/[0.02] p-6">{children}</div>
      </div>
    </section>
    <V2Footer lang="tr" />
  </div>
);

const AuthField = ({ label, type, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required
      className="w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition"
      data-testid={testid}
    />
  </label>
);

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.8-2 13.3-5.2l-6.2-5.2c-1.9 1.3-4.4 2.1-7.1 2.1-5.2 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2c-.4.4 6.5-4.7 6.5-14.7 0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);
